import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertTriangle, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { QboTargetPicker } from './QboTargetPicker';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function QboRegionVerificationPanel({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState({}); // region key -> {id, name}
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/qbo-live/regions`, { headers: authHeaders() });
      setData(data);
    } catch (err) {
      toast.error(getApiError(err, 'Could not fetch QBO Departments.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async (region) => {
    const choice = picked[region.key] || (region.suggestion && !region.ambiguous ? { id: region.suggestion.id, name: region.suggestion.name } : null);
    if (!choice) return;
    setSaving(region.key);
    try {
      let itemId = region.current?.item_id;
      if (itemId) {
        await axios.patch(`${API}/api/sales-invoicing/reference-lists/location/items/${itemId}`, { external_code: choice.id }, { headers: authHeaders() });
      } else {
        const { data: added } = await axios.post(`${API}/api/sales-invoicing/reference-lists/location/items`, { name: region.label, external_code: choice.id }, { headers: authHeaders() });
        itemId = added.id;
      }
      await axios.post(`${API}/api/sales-invoicing/reference-lists/location/items/${itemId}/verify`, { verified: true }, { headers: authHeaders() });
      toast.success(`${region.label} verified against QBO ${choice.name}.`);
      await load();
    } catch (err) {
      toast.error(getApiError(err, 'Could not save that verification.'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="qbo-region-dialog">
        <DialogHeader>
          <DialogTitle>Verify AZ Regions against QBO Departments</DialogTitle>
        </DialogHeader>

        {loading && <div className="text-sm text-slate-500" data-testid="qbo-region-loading">Fetching live QBO Departments…</div>}

        {!loading && data && (
          <div className="space-y-4" data-testid="qbo-region-content">
            <div className="text-xs text-slate-500">
              {data.departments.length} QBO Department(s) found in the {data.environment} company. Nothing is applied automatically — pick each Region's real QBO ID yourself, then Verify.
            </div>
            {data.regions.map((region) => {
              const candidates = data.departments.map((d) => ({ id: d.id, name: d.name }));
              const choice = picked[region.key];
              const prefillName = !region.is_verified && !choice && region.suggestion && !region.ambiguous ? region.suggestion.name : (choice?.name || null);
              return (
                <div key={region.key} className="border rounded-lg p-3 space-y-2" data-testid={`qbo-region-row-${region.key}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{region.label}</span>
                    {region.is_verified ? (
                      <Badge className="bg-emerald-100 text-emerald-800" data-testid={`qbo-region-${region.key}-verified-badge`}><CheckCircle2 className="h-3 w-3 mr-1" />Verified</Badge>
                    ) : region.ambiguous ? (
                      <Badge variant="destructive" data-testid={`qbo-region-${region.key}-ambiguous-badge`}><AlertTriangle className="h-3 w-3 mr-1" />Ambiguous — pick one</Badge>
                    ) : !region.suggestion ? (
                      <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid={`qbo-region-${region.key}-missing-badge`}><HelpCircle className="h-3 w-3 mr-1" />No clear match</Badge>
                    ) : (
                      <Badge variant="secondary" data-testid={`qbo-region-${region.key}-suggested-badge`}>Suggested ({Math.round(region.suggestion.confidence * 100)}%)</Badge>
                    )}
                  </div>

                  {region.ambiguous && (
                    <div className="text-xs text-red-700" data-testid={`qbo-region-${region.key}-tied-list`}>
                      Tied: {region.tied_candidates.map((c) => c.name).join(', ')} — select the right one below.
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <QboTargetPicker
                      candidates={candidates}
                      disabled={saving === region.key}
                      selectedName={prefillName}
                      onPick={(id, name) => setPicked((p) => ({ ...p, [region.key]: { id, name } }))}
                      testKey={`qbo-region-${region.key}`}
                    />
                    <Button
                      size="sm"
                      disabled={saving === region.key || (!choice && !(region.suggestion && !region.ambiguous))}
                      onClick={() => verify(region)}
                      data-testid={`qbo-region-${region.key}-verify-btn`}
                    >
                      {region.is_verified ? 'Re-verify' : 'Verify'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
